/**
 * useIncidentAudit Hook - Phase 13
 * 
 * React hook for incident audit trail data and actions.
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import {
  getAllIncidents,
  getIncidentById,
  getIncidentSummary,
  getFilterOptions,
  getIncidentTimeline,
  invalidateIncidentCache,
  updateIncident,
  SORT_OPTIONS
} from '@/lib/offlineAudit';
import {
  downloadIncidentsCsv,
  downloadIncidentsJson,
  copySummaryToClipboard
} from '@/lib/offlineAudit/exportEngine';
import { FOLLOWUP_STATUS } from '@/lib/offlineAudit/incidentModel';

/**
 * useIncidentAudit Hook
 */
export function useIncidentAudit(options = {}) {
  const {
    autoRefresh = true,
    refreshInterval = 10000,
    initialFilters = {}
  } = options;

  // State
  const [incidents, setIncidents] = useState([]);
  const [summary, setSummary] = useState(null);
  const [filterOptions, setFilterOptions] = useState(null);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [lastRefresh, setLastRefresh] = useState(null);
  
  const [filters, setFilters] = useState({
    status: null,
    severity: null,
    domain: null,
    sourceType: null,
    locationId: null,
    terminalId: null,
    actorUserId: null,
    dateFrom: null,
    dateTo: null,
    searchText: '',
    unresolvedOnly: false,
    followUpStatus: null,
    sortBy: SORT_OPTIONS.NEWEST_FIRST,
    ...initialFilters
  });

  // Refresh data
  const refreshData = useCallback(async () => {
    try {
      const [incidentData, summaryData, filterOpts] = await Promise.all([
        getAllIncidents(filters),
        getIncidentSummary(),
        getFilterOptions()
      ]);
      
      setIncidents(incidentData);
      setSummary(summaryData);
      setFilterOptions(filterOpts);
      setLastRefresh(Date.now());
    } catch (error) {
      console.error('[useIncidentAudit] Refresh failed:', error);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  // Initial load and auto-refresh
  useEffect(() => {
    refreshData();
    
    if (autoRefresh) {
      const interval = setInterval(refreshData, refreshInterval);
      return () => clearInterval(interval);
    }
  }, [refreshData, autoRefresh, refreshInterval]);

  // Refresh when filters change
  useEffect(() => {
    setLoading(true);
    refreshData();
  }, [refreshData]);

  // Timeline view
  const timeline = useMemo(() => {
    const buckets = {
      today: [],
      yesterday: [],
      thisWeek: [],
      older: []
    };
    
    const now = Date.now();
    const day = 24 * 60 * 60 * 1000;
    const todayStart = new Date().setHours(0, 0, 0, 0);
    const yesterdayStart = todayStart - day;
    const weekStart = todayStart - 6 * day;
    
    for (const incident of incidents) {
      const ts = incident.occurredAt;
      
      if (ts >= todayStart) {
        buckets.today.push(incident);
      } else if (ts >= yesterdayStart) {
        buckets.yesterday.push(incident);
      } else if (ts >= weekStart) {
        buckets.thisWeek.push(incident);
      } else {
        buckets.older.push(incident);
      }
    }
    
    return buckets;
  }, [incidents]);

  // Grouped views
  const groupedByDomain = useMemo(() => {
    const groups = {};
    for (const incident of incidents) {
      const key = incident.domain || 'other';
      if (!groups[key]) groups[key] = [];
      groups[key].push(incident);
    }
    return groups;
  }, [incidents]);

  const groupedByLocation = useMemo(() => {
    const groups = {};
    for (const incident of incidents) {
      const key = incident.locationId || 'no-location';
      if (!groups[key]) groups[key] = [];
      groups[key].push(incident);
    }
    return groups;
  }, [incidents]);

  // Categorized incidents
  const openIncidents = useMemo(() => 
    incidents.filter(i => ['queued', 'retrying', 'failed', 'conflict', 'manual_review'].includes(i.status)),
    [incidents]
  );

  const criticalIncidents = useMemo(() => 
    incidents.filter(i => i.severity === 'critical'),
    [incidents]
  );

  const failedIncidents = useMemo(() => 
    incidents.filter(i => i.status === 'failed'),
    [incidents]
  );

  const conflictIncidents = useMemo(() => 
    incidents.filter(i => i.status === 'conflict'),
    [incidents]
  );

  const needsAttentionIncidents = useMemo(() => 
    incidents.filter(i => 
      i.status === 'failed' || 
      i.status === 'conflict' ||
      i.status === 'manual_review' ||
      i.followUpStatus === 'needs_followup' ||
      i.followUpStatus === 'escalated'
    ),
    [incidents]
  );

  // Actions

  /**
   * Update filters
   */
  const updateFilters = useCallback((newFilters) => {
    setFilters(prev => ({ ...prev, ...newFilters }));
  }, []);

  /**
   * Clear all filters
   */
  const clearFilters = useCallback(() => {
    setFilters({
      status: null,
      severity: null,
      domain: null,
      sourceType: null,
      locationId: null,
      terminalId: null,
      actorUserId: null,
      dateFrom: null,
      dateTo: null,
      searchText: '',
      unresolvedOnly: false,
      followUpStatus: null,
      sortBy: SORT_OPTIONS.NEWEST_FIRST
    });
  }, []);

  /**
   * Add manager note to incident
   */
  const addNote = useCallback(async (incidentId, note, actor) => {
    try {
      const queueItemId = incidentId.replace('inc_', '');
      await updateIncident(queueItemId, {
        managerNote: note,
        noteAddedBy: actor,
        noteAddedAt: Date.now()
      });
      invalidateIncidentCache();
      await refreshData();
      return { success: true };
    } catch (error) {
      return { success: false, error: error.message };
    }
  }, [refreshData]);

  /**
   * Mark incident as reviewed
   */
  const markReviewed = useCallback(async (incidentId, actor) => {
    try {
      const queueItemId = incidentId.replace('inc_', '');
      await updateIncident(queueItemId, {
        manualResolutionStatus: FOLLOWUP_STATUS.REVIEWED,
        reviewedBy: actor,
        reviewedAt: Date.now()
      });
      invalidateIncidentCache();
      await refreshData();
      return { success: true };
    } catch (error) {
      return { success: false, error: error.message };
    }
  }, [refreshData]);

  /**
   * Mark incident as needs follow-up
   */
  const markNeedsFollowup = useCallback(async (incidentId, actor, note) => {
    try {
      const queueItemId = incidentId.replace('inc_', '');
      await updateIncident(queueItemId, {
        manualResolutionStatus: FOLLOWUP_STATUS.NEEDS_FOLLOWUP,
        reviewedBy: actor,
        reviewedAt: Date.now(),
        managerNote: note || undefined
      });
      invalidateIncidentCache();
      await refreshData();
      return { success: true };
    } catch (error) {
      return { success: false, error: error.message };
    }
  }, [refreshData]);

  /**
   * Mark incident as escalated
   */
  const markEscalated = useCallback(async (incidentId, actor, reason) => {
    try {
      const queueItemId = incidentId.replace('inc_', '');
      await updateIncident(queueItemId, {
        manualResolutionStatus: FOLLOWUP_STATUS.ESCALATED,
        reviewedBy: actor,
        reviewedAt: Date.now(),
        escalationReason: reason
      });
      invalidateIncidentCache();
      await refreshData();
      return { success: true };
    } catch (error) {
      return { success: false, error: error.message };
    }
  }, [refreshData]);

  /**
   * Dismiss incident operationally
   */
  const dismissIncident = useCallback(async (incidentId, actor, reason) => {
    try {
      const queueItemId = incidentId.replace('inc_', '');
      await updateIncident(queueItemId, {
        manualResolutionStatus: FOLLOWUP_STATUS.DISMISSED,
        reviewedBy: actor,
        reviewedAt: Date.now(),
        dismissReason: reason
      });
      invalidateIncidentCache();
      await refreshData();
      return { success: true };
    } catch (error) {
      return { success: false, error: error.message };
    }
  }, [refreshData]);

  /**
   * Export incidents as CSV
   */
  const exportCsv = useCallback(async () => {
    setExporting(true);
    try {
      const result = await downloadIncidentsCsv(filters);
      return result;
    } finally {
      setExporting(false);
    }
  }, [filters]);

  /**
   * Export incidents as JSON
   */
  const exportJson = useCallback(async () => {
    setExporting(true);
    try {
      const result = await downloadIncidentsJson(filters);
      return result;
    } finally {
      setExporting(false);
    }
  }, [filters]);

  /**
   * Copy summary to clipboard
   */
  const copySummary = useCallback(async () => {
    return await copySummaryToClipboard(filters);
  }, [filters]);

  /**
   * Get incident by ID
   */
  const getIncident = useCallback(async (incidentId) => {
    return await getIncidentById(incidentId);
  }, []);

  return {
    // Data
    incidents,
    summary,
    filterOptions,
    timeline,
    groupedByDomain,
    groupedByLocation,
    
    // Categorized
    openIncidents,
    criticalIncidents,
    failedIncidents,
    conflictIncidents,
    needsAttentionIncidents,
    
    // State
    loading,
    exporting,
    lastRefresh,
    filters,
    
    // Filter actions
    updateFilters,
    setFilters,
    clearFilters,
    
    // Review actions
    addNote,
    markReviewed,
    markNeedsFollowup,
    markEscalated,
    dismissIncident,
    
    // Export actions
    exportCsv,
    exportJson,
    copySummary,
    
    // Other actions
    refreshData,
    getIncident
  };
}

export default useIncidentAudit;
